import React, { useMemo, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { v4 as uuidv4 } from 'uuid';

import { translate as t } from 'hooks/useTranslations';
import errorHelper from 'helpers/errorHelper';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import useOrganizations from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganizations';
import useJurisdictions from 'data_sources/useJurisdictions';

import type { Organization } from 'views/App/Portal/LexID/Organizations/_types/organization';

type OrganizationWithKey = Organization & { _uniqueKey: string; };

interface AvailableOrganizationsTabProps {
  debouncedSearch: string;
  debouncedJurisdiction: string;
  selectedOrganizations: Organization[];
  toggleOrganization: (org: Organization) => void;
  isSelected: (orgId: string) => boolean;
  existingOrgIds: string[];
  setAvailableJurisdictions: React.Dispatch<React.SetStateAction<Array<{ value: string; label: string; }>>>;
}

const AvailableOrganizationsTab: React.FC<AvailableOrganizationsTabProps> = ({
  debouncedSearch,
  debouncedJurisdiction,
  selectedOrganizations,
  toggleOrganization,
  isSelected,
  existingOrgIds,
  setAvailableJurisdictions,
}) => {
  const [organizationsData, setOrganizationsData] = useState<OrganizationWithKey[]>([]);

  // Build params for API call
  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (debouncedSearch) p.search = debouncedSearch;
    if (debouncedJurisdiction) p.state = debouncedJurisdiction;
    return p;
  }, [debouncedSearch, debouncedJurisdiction]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isFetching, error } =
    useOrganizations<Organization>(params, {}, (errors) => {
      errorHelper.enqueueErrors(errors);
    });

  // Fetch jurisdictions for filter dropdown
  const { data: jurisdictions } = useJurisdictions({}, (errors) => {
    errorHelper.enqueueErrors(errors);
  });

  // Flatten data from pages and add unique keys to handle backend duplicates
  useEffect(() => {
    if (data && !isError) {
      const allOrganizations = data.pages.flatMap((page) => page.content) || [];

      // Add unique key to each organization to prevent React key conflicts
      const organizationsWithKeys: OrganizationWithKey[] = allOrganizations.map(org => ({
        ...org,
        _uniqueKey: uuidv4(),
      }));

      setOrganizationsData(organizationsWithKeys);
    }
  }, [data, isError]);

  // Set available jurisdictions from jurisdictions endpoint
  useEffect(() => {
    if (jurisdictions) {
      setAvailableJurisdictions(
        jurisdictions.map((jurisdiction) => ({
          value: jurisdiction.abbreviation,
          label: jurisdiction.name,
        })).sort((a, b) => a.label.localeCompare(b.label))
      );
    }
  }, [jurisdictions, setAvailableJurisdictions]);

  // Create a map of state abbreviation to state name for the grid
  const stateMap = useMemo(() => {
    if (!jurisdictions) return {};
    return jurisdictions.reduce((acc, jurisdiction) => {
      acc[jurisdiction.abbreviation] = jurisdiction.name;
      return acc;
    }, {} as Record<string, string>);
  }, [jurisdictions]);

  // Column definitions
  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('riskPoolSharing.properties.organizationName'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const orgId = row.organization?.id;
          const isInPool = existingOrgIds.includes(orgId);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {row.organization?.name}
              </Box>
              {isInPool && (
                <Chip
                  label={t('riskPoolSharing.add.messages.alreadyInPool')}
                  size="small"
                  color="default"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          );
        },
      },
      state: {
        header: t('riskPoolSharing.properties.jurisdiction'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const stateAbbr = row.organization?.state;
          const stateName = stateAbbr ? stateMap[stateAbbr] || stateAbbr : '-';
          return <Box component="span">{stateName}</Box>;
        },
      },
      city: {
        header: t('riskPoolSharing.properties.city'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Box component="span">{row.organization?.city || '-'}</Box>;
        },
      },
      lastUpdate: {
        header: t('riskPoolSharing.properties.updated'),
        flex: 0.8,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row || !row.organization?.lastUpdate) return null;
          return (
            <Box component="span">
              {new Date(row.organization.lastUpdate).toLocaleDateString()}
            </Box>
          );
        },
      },
    }),
    [existingOrgIds, stateMap, t]
  );

  // Handle selection - map unique keys to organization IDs
  const selectedUniqueKeys = useMemo(() => {
    return organizationsData
      .filter((org) => isSelected(org.organization.id))
      .map((org) => org._uniqueKey);
  }, [selectedOrganizations, organizationsData, isSelected]);

  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const newSelectedUniqueKeys = selectionModel as string[];

    // Find organizations that were toggled on
    newSelectedUniqueKeys.forEach((uniqueKey) => {
      const org = organizationsData.find((o) => o._uniqueKey === uniqueKey);
      if (org && !isSelected(org.organization.id)) {
        toggleOrganization(org);
      }
    });

    // Find organizations that were toggled off
    selectedUniqueKeys.forEach((uniqueKey) => {
      if (!newSelectedUniqueKeys.includes(uniqueKey)) {
        const org = organizationsData.find((o) => o._uniqueKey === uniqueKey);
        if (org) {
          toggleOrganization(org);
        }
      }
    });
  };

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: hasNextPage || false,
      isFetchingNextPage,
      fetchNextPage,
      isError,
      isLoading: isFetching && !isFetchingNextPage, // Show loading when fetching new data (filters changed) but not when loading more pages
      error,
      data: {
        pages: data?.pages.map((page) => ({
          count: page.count,
          rows: page.content,
        })) || [],
      },
    }),
    [hasNextPage, isFetchingNextPage, fetchNextPage, isError, isFetching, error, data]
  );

  return (
    <Box sx={{ minHeight: 0, height: '100%' }}>
      <GridSection
        columns={columns}
        data={organizationsData}
        query={gridViewQuery}
        gridProps={{
          checkboxSelection: true,
          rowSelectionModel: selectedUniqueKeys,
          onRowSelectionModelChange: handleSelectionChange,
          getRowId: (row: OrganizationWithKey) => row._uniqueKey,
          isRowSelectable: (params: any) => !existingOrgIds.includes(params.row.organization.id),
        }}
      />
    </Box>
  );
};

export default AvailableOrganizationsTab;

